// Liste compacte des CVE d'un logiciel, sur la fiche appareil.
//
// Repliée : les trois plus graves (CVSS décroissant), liées à leur fiche. Un
// logiciel peut porter des milliers de CVE sur un seul poste (12 265 mesurées
// pour le noyau Linux) : le « +N » déplie la liste complète, mais plafonnée à
// MAX_LISTED lignes — au-delà, le DOM ne sert plus à rien et la page
// Vulnérabilités est le bon endroit pour fouiller.

import { useMemo, useState } from 'react';
import { cveUrl } from '../lib/format.jsx';

const TOP = 3;
const MAX_LISTED = 200;

const scoreTone = (sev) => {
  const s = String(sev || '').toUpperCase();
  if (s === 'CRITICAL') return 'text-crit';
  if (s === 'HIGH') return 'text-warn';
  return 'text-muted';
};

const tooltipOf = (c) => {
  const head = [
    c.cveId,
    c.cvssScore != null ? `CVSS ${Number(c.cvssScore).toFixed(1)}` : null,
    c.cvssSeverity || null,
  ]
    .filter(Boolean)
    .join(' • ');
  const desc = (c.cveDescription || '').trim();
  return desc ? `${head}\n${desc.length > 160 ? desc.slice(0, 160) + '…' : desc}` : head;
};

export function CveLinks({ cves }) {
  const [open, setOpen] = useState(false);
  // Le tri ne coûte que si la liste est consultée : useMemo évite de retrier
  // 12 000 lignes à chaque rafraîchissement de 15 s de la fiche.
  const sorted = useMemo(
    () => [...cves].sort((a, b) => (b.cvssScore || 0) - (a.cvssScore || 0)),
    [cves]
  );
  const rest = sorted.length - TOP;

  const link = (c) => (
    <a
      key={c.id ?? c.cveId}
      href={cveUrl(c.cveId)}
      target="_blank"
      rel="noopener noreferrer"
      title={tooltipOf(c)}
      className="text-blue-400 no-underline hover:underline font-mono"
    >
      {c.cveId}
    </a>
  );

  return (
    <div className="text-[10px] text-muted-2">
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
        {sorted.slice(0, TOP).map(link)}
        {rest > 0 && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="bg-transparent border-none p-0 cursor-pointer text-muted hover:text-fg-2 font-semibold text-[10px]"
            aria-expanded={open}
          >
            {open ? 'réduire' : `+${rest}`}
          </button>
        )}
      </div>
      {open && (
        <div className="mt-1 max-h-[220px] overflow-y-auto border border-rule rounded-lg bg-sidebar px-2 py-1.5 flex flex-col gap-0.5">
          {sorted.slice(0, MAX_LISTED).map((c) => (
            <div key={c.id ?? c.cveId} className="flex items-center gap-2">
              <span className={`w-7 text-right font-mono font-semibold ${scoreTone(c.cvssSeverity)}`}>
                {c.cvssScore != null ? Number(c.cvssScore).toFixed(1) : '—'}
              </span>
              {link(c)}
            </div>
          ))}
          {sorted.length > MAX_LISTED && (
            <div className="text-muted-2 italic pt-1">
              {`… et ${sorted.length - MAX_LISTED} autres (voir la page Vulnérabilités)`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
